import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Festive illustrations (1.13): birthday, Advent, Nikolaus, Christmas, New Year's Eve / New
 * Year, Easter, Halloween. Drawn in SVG from the festive tokens – no images, crisp on the wall,
 * calm motion (balloons rising, snow falling, candles flickering, fireworks) that stops with
 * reduced motion.
 *
 *  - backdrop: fills its parent behind the resting wall's text (art at the edges and bottom)
 *  - card:     the picture on top of the festive view
 *  - badge:    a small motif for the band on "Heute"
 */
export type FestiveKind =
  | 'birthday'
  | 'advent'
  | 'nikolaus'
  | 'christmas'
  | 'newyearseve'
  | 'newyear'
  | 'easter'
  | 'halloween';

export interface FestiveSceneProps {
  kind: FestiveKind;
  variant?: 'backdrop' | 'card' | 'badge';
  /** Advent: candles lit (1–4) */
  candles?: number;
  className?: string;
}

const c = (name: string) => `var(--shh-color-festive-${name})`;
const BALLOONS = ['balloon1', 'balloon2', 'balloon3', 'balloon4', 'balloon5'];

/** The sky behind a scene – deep, like the resting wall's own sky. */
function Sky({ id, top, bottom }: { id: string; top: string; bottom: string }) {
  return (
    <>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c(top)} />
          <stop offset="1" stopColor={c(bottom)} />
        </linearGradient>
      </defs>
      <rect width="1600" height="900" fill={`url(#${id})`} />
    </>
  );
}

function Snow({ count = 60, seed = 1 }: { count?: number; seed?: number }) {
  return (
    <g fill={c('snow')}>
      {Array.from({ length: count }, (_, i) => {
        const x = (i * 137 * seed) % 1600;
        const y = (i * 71 + seed * 13) % 900;
        const r = 2 + ((i * 7) % 5);
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={r}
            opacity={0.5 + ((i * 3) % 5) / 10}
            className="shh-festive-snow"
            style={{
              animationDelay: `${-((i * 1.7) % 14)}s`,
              animationDuration: `${10 + (i % 6) * 2}s`,
            }}
          />
        );
      })}
    </g>
  );
}

function Stars({ count = 40 }: { count?: number }) {
  return (
    <g fill={c('glow')}>
      {Array.from({ length: count }, (_, i) => (
        <circle
          key={i}
          cx={(i * 211) % 1600}
          cy={(i * 97) % 420}
          r={1.5 + (i % 3)}
          className="shh-festive-twinkle"
          style={{ animationDelay: `${-((i * 0.7) % 5)}s` }}
        />
      ))}
    </g>
  );
}

/** A candle with a flickering flame, standing on (x, base). */
function Candle({ x, base, h = 150, lit }: { x: number; base: number; h?: number; lit: boolean }) {
  return (
    <g>
      <rect x={x - 18} y={base - h} width={36} height={h} rx={6} fill={c('candle')} />
      <rect x={x - 18} y={base - h} width={10} height={h} rx={5} fill={c('berry')} opacity={0.6} />
      <line x1={x} y1={base - h} x2={x} y2={base - h - 14} stroke={c('ink')} strokeWidth={3} />
      {lit && (
        <g className="shh-festive-flicker" style={{ transformOrigin: `${x}px ${base - h - 14}px` }}>
          <circle cx={x} cy={base - h - 34} r={46} fill={c('glow')} opacity={0.25} />
          <path
            d={`M${x} ${base - h - 70} C ${x + 18} ${base - h - 40}, ${x + 14} ${base - h - 14}, ${x} ${base - h - 12} C ${x - 14} ${base - h - 14}, ${x - 18} ${base - h - 40}, ${x} ${base - h - 70} Z`}
            fill={c('flame')}
          />
          <path
            d={`M${x} ${base - h - 50} C ${x + 8} ${base - h - 34}, ${x + 6} ${base - h - 20}, ${x} ${base - h - 18} C ${x - 6} ${base - h - 20}, ${x - 8} ${base - h - 34}, ${x} ${base - h - 50} Z`}
            fill={c('snow')}
            opacity={0.8}
          />
        </g>
      )}
    </g>
  );
}

function Wreath({ cx, cy, lit }: { cx: number; cy: number; lit: number }) {
  const leaves = Array.from({ length: 46 }, (_, i) => {
    const a = (i / 46) * Math.PI * 2;
    return { x: cx + Math.cos(a) * 250, y: cy + Math.sin(a) * 62, r: (a * 180) / Math.PI };
  });
  return (
    <g>
      <ellipse cx={cx} cy={cy + 10} rx={300} ry={90} fill={c('ink')} opacity={0.35} />
      {leaves.map((l, i) => (
        <ellipse
          key={i}
          cx={l.x}
          cy={l.y}
          rx={46}
          ry={18}
          transform={`rotate(${l.r + 70} ${l.x} ${l.y})`}
          fill={i % 2 ? c('fir') : c('fir-light')}
        />
      ))}
      {leaves
        .filter((_, i) => i % 5 === 0)
        .map((l, i) => (
          <circle key={i} cx={l.x} cy={l.y + 6} r={11} fill={c('berry')} />
        ))}
      {[-180, -60, 60, 180].map((dx, i) => (
        <Candle key={dx} x={cx + dx} base={cy + (i === 1 || i === 2 ? 12 : -4)} lit={i < lit} />
      ))}
    </g>
  );
}

function Tree({ x, base, s = 1 }: { x: number; base: number; s?: number }) {
  const layer = (w: number, top: number, bottom: number, key: number) => (
    <path
      key={key}
      d={`M${x} ${top} L ${x + w / 2} ${bottom} L ${x - w / 2} ${bottom} Z`}
      fill={c(key % 2 ? 'fir-light' : 'fir')}
    />
  );
  return (
    <g transform={`translate(${x} ${base}) scale(${s}) translate(${-x} ${-base})`}>
      <rect x={x - 22} y={base - 60} width={44} height={60} fill={c('wood')} />
      {layer(420, base - 330, base - 60, 0)}
      {layer(340, base - 450, base - 220, 1)}
      {layer(250, base - 560, base - 360, 2)}
      <path
        d={`M${x} ${base - 610} l 14 30 32 4 -24 22 7 32 -29 -16 -29 16 7 -32 -24 -22 32 -4 Z`}
        fill={c('gold')}
        className="shh-festive-twinkle"
      />
      {Array.from({ length: 22 }, (_, i) => {
        const row = i % 4;
        const t = (i * 0.37) % 1;
        const width = [380, 300, 220, 140][row]!;
        const y = base - 110 - row * 110 - t * 30;
        return (
          <circle
            key={i}
            cx={x - width / 2 + t * width}
            cy={y}
            r={10}
            fill={c(i % 3 === 0 ? 'berry' : i % 3 === 1 ? 'gold' : 'glow')}
            className="shh-festive-twinkle"
            style={{ animationDelay: `${-((i * 0.9) % 4)}s` }}
          />
        );
      })}
    </g>
  );
}

function Gift({
  x,
  base,
  w,
  h,
  color,
}: {
  x: number;
  base: number;
  w: number;
  h: number;
  color: string;
}) {
  return (
    <g>
      <rect x={x} y={base - h} width={w} height={h} rx={6} fill={c(color)} />
      <rect x={x + w / 2 - 8} y={base - h} width={16} height={h} fill={c('gold')} />
      <rect x={x} y={base - h * 0.62} width={w} height={14} fill={c('gold')} />
      <path
        d={`M${x + w / 2} ${base - h} c -30 -34 -60 -6 0 0 c 60 -6 30 -34 0 0`}
        fill="none"
        stroke={c('gold')}
        strokeWidth={8}
      />
    </g>
  );
}

function Balloon({ x, y, color, delay }: { x: number; y: number; color: string; delay: number }) {
  return (
    <g className="shh-festive-float" style={{ animationDelay: `${delay}s` }}>
      <path
        d={`M${x} ${y + 92} q 18 60 -6 120 q -20 60 8 130`}
        fill="none"
        stroke={c('snow')}
        strokeOpacity={0.6}
        strokeWidth={3}
      />
      <ellipse cx={x} cy={y} rx={66} ry={82} fill={c(color)} />
      <ellipse cx={x - 22} cy={y - 30} rx={14} ry={24} fill={c('snow')} opacity={0.35} />
      <path d={`M${x - 10} ${y + 84} L ${x + 10} ${y + 84} L ${x} ${y + 96} Z`} fill={c(color)} />
    </g>
  );
}

function Bunting({ y = 0 }: { y?: number }) {
  const flags = Array.from({ length: 18 }, (_, i) => i);
  return (
    <g>
      <path
        d={`M0 ${y + 40} Q 800 ${y + 160} 1600 ${y + 40}`}
        fill="none"
        stroke={c('snow')}
        strokeOpacity={0.6}
        strokeWidth={4}
      />
      {flags.map((i) => {
        const t = (i + 0.5) / flags.length;
        const x = t * 1600;
        const yy = y + 40 + 4 * 120 * t * (1 - t);
        return (
          <path
            key={i}
            d={`M${x - 36} ${yy} L ${x + 36} ${yy} L ${x} ${yy + 80} Z`}
            fill={c(BALLOONS[i % 5]!)}
          />
        );
      })}
    </g>
  );
}

function Confetti({ count = 50 }: { count?: number }) {
  return (
    <g>
      {Array.from({ length: count }, (_, i) => (
        <rect
          key={i}
          x={(i * 157) % 1600}
          y={(i * 83) % 900}
          width={10}
          height={18}
          rx={3}
          fill={c(BALLOONS[i % 5]!)}
          className="shh-festive-snow"
          style={{
            animationDelay: `${-((i * 1.3) % 12)}s`,
            animationDuration: `${8 + (i % 5) * 2}s`,
          }}
        />
      ))}
    </g>
  );
}

function Cake({ x, base }: { x: number; base: number }) {
  return (
    <g>
      <rect
        x={x - 170}
        y={base - 30}
        width={340}
        height={30}
        rx={12}
        fill={c('snow')}
        opacity={0.85}
      />
      <rect x={x - 140} y={base - 170} width={280} height={140} rx={16} fill={c('cake')} />
      <path
        d={`M${x - 140} ${base - 150} q 35 40 70 0 q 35 40 70 0 q 35 40 70 0 q 35 40 70 0 v -20 h -280 Z`}
        fill={c('icing')}
      />
      {[-80, -26, 26, 80].map((dx, i) => (
        <g key={dx}>
          <rect
            x={x + dx - 7}
            y={base - 230}
            width={14}
            height={60}
            rx={4}
            fill={c(BALLOONS[i]!)}
          />
          <g
            className="shh-festive-flicker"
            style={{ transformOrigin: `${x + dx}px ${base - 232}px` }}
          >
            <path
              d={`M${x + dx} ${base - 272} c 12 18 10 34 0 38 c -10 -4 -12 -20 0 -38 Z`}
              fill={c('flame')}
            />
          </g>
        </g>
      ))}
    </g>
  );
}

function Fireworks() {
  const bursts = [
    { x: 160, y: 200, color: 'gold', d: 0 },
    { x: 1450, y: 180, color: 'berry', d: 1.1 },
    { x: 1250, y: 420, color: 'balloon3', d: 2.2 },
    { x: 1040, y: 110, color: 'glow', d: 3.1 },
    { x: 330, y: 520, color: 'balloon5', d: 1.7 },
    { x: 1500, y: 560, color: 'gold', d: 0.6 },
    { x: 800, y: 90, color: 'balloon4', d: 2.7 },
  ];
  return (
    <g>
      {bursts.map((b, i) => (
        <g
          key={i}
          className="shh-festive-burst"
          style={{ transformOrigin: `${b.x}px ${b.y}px`, animationDelay: `${b.d}s` }}
        >
          {Array.from({ length: 16 }, (_, k) => {
            const a = (k / 16) * Math.PI * 2;
            return (
              <line
                key={k}
                x1={b.x + Math.cos(a) * 30}
                y1={b.y + Math.sin(a) * 30}
                x2={b.x + Math.cos(a) * 150}
                y2={b.y + Math.sin(a) * 150}
                stroke={c(b.color)}
                strokeWidth={6}
                strokeLinecap="round"
              />
            );
          })}
        </g>
      ))}
    </g>
  );
}

function Skyline() {
  // low, so the city stays under the clock
  const blocks = [
    [0, 110],
    [90, 150],
    [170, 120],
    [260, 180],
    [360, 130],
    [450, 160],
    [560, 115],
    [640, 200],
    [760, 140],
    [860, 170],
    [960, 125],
    [1050, 190],
    [1160, 150],
    [1260, 120],
    [1350, 165],
    [1460, 135],
  ] as const;
  return (
    <g fill={c('ink')}>
      {blocks.map(([x, h], i) => (
        <rect key={i} x={x} y={900 - h} width={i % 2 ? 100 : 90} height={h} />
      ))}
      {blocks.map(([x, h], i) =>
        Array.from({ length: 4 }, (_, k) => (
          <rect
            key={`${i}-${k}`}
            x={x + 20 + (k % 2) * 34}
            y={900 - h + 30 + Math.floor(k / 2) * 50}
            width={14}
            height={20}
            fill={c('glow')}
            opacity={(i + k) % 3 ? 0.7 : 0.15}
          />
        )),
      )}
    </g>
  );
}

function Egg({
  x,
  base,
  color,
  stripe,
  r = 0,
}: {
  x: number;
  base: number;
  color: string;
  stripe: string;
  r?: number;
}) {
  return (
    <g transform={`rotate(${r} ${x} ${base - 50})`}>
      <ellipse cx={x} cy={base - 50} rx={40} ry={52} fill={c(color)} />
      <path
        d={`M${x - 38} ${base - 60} q 38 -22 76 0`}
        fill="none"
        stroke={c(stripe)}
        strokeWidth={10}
      />
      <path
        d={`M${x - 36} ${base - 34} q 36 22 72 0`}
        fill="none"
        stroke={c(stripe)}
        strokeWidth={8}
      />
    </g>
  );
}

function Flower({ x, base, color }: { x: number; base: number; color: string }) {
  return (
    <g>
      <line x1={x} y1={base} x2={x} y2={base - 70} stroke={c('fir')} strokeWidth={6} />
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <circle
            key={i}
            cx={x + Math.cos(a) * 18}
            cy={base - 80 + Math.sin(a) * 18}
            r={14}
            fill={c(color)}
          />
        );
      })}
      <circle cx={x} cy={base - 80} r={11} fill={c('gold')} />
    </g>
  );
}

function Pumpkin({ x, base, s = 1 }: { x: number; base: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${base}) scale(${s})`}>
      <rect x={-8} y={-190} width={16} height={36} rx={6} fill={c('fir')} />
      {[-70, 0, 70].map((dx) => (
        <ellipse
          key={dx}
          cx={dx * 0.9}
          cy={-90}
          rx={86}
          ry={92}
          fill={c('pumpkin')}
          stroke={c('wood')}
          strokeOpacity={0.4}
          strokeWidth={4}
        />
      ))}
      <g fill={c('flame')} className="shh-festive-flicker" style={{ transformOrigin: '0px -80px' }}>
        <path d="M-60 -120 l 30 0 -15 -28 Z" />
        <path d="M30 -120 l 30 0 -15 -28 Z" />
        <path d="M-60 -60 q 60 40 120 0 q -20 10 -30 -6 l -10 12 -10 -12 -10 12 -10 -12 -10 12 Z" />
      </g>
    </g>
  );
}

function Boot({ x, base }: { x: number; base: number }) {
  return (
    <g>
      <Gift x={x - 70} base={base - 230} w={70} h={70} color="balloon3" />
      <rect x={x + 10} y={base - 330} width={30} height={110} rx={6} fill={c('cake')} />
      <circle cx={x + 25} cy={base - 340} r={26} fill={c('berry')} />
      <path
        d={`M${x - 90} ${base - 260} h 180 v 170 c 0 40 40 40 110 50 c 40 6 40 40 40 40 h -330 Z`}
        fill={c('candle')}
      />
      <rect x={x - 100} y={base - 290} width={200} height={50} rx={14} fill={c('snow')} />
      <rect x={x - 90} y={base - 20} width={330} height={20} rx={8} fill={c('ink')} />
    </g>
  );
}

const SCENES: Record<FestiveKind, (lit: number) => ReactNode> = {
  birthday: () => (
    <>
      <Sky id="sky-party" top="party-top" bottom="party-bottom" />
      <Confetti />
      <Bunting />
      {[120, 330, 1270, 1480, 1080].map((x, i) => (
        <Balloon key={x} x={x} y={560 - (i % 2) * 140} color={BALLOONS[i]!} delay={-i * 1.6} />
      ))}
      <Cake x={1400} base={900} />
    </>
  ),
  advent: (lit) => (
    <>
      <Sky id="sky-advent" top="night-top" bottom="night-bottom" />
      <Stars />
      <Snow />
      <ellipse cx="800" cy="900" rx="900" ry="140" fill={c('snow')} opacity={0.18} />
      <g transform="translate(1260 840) scale(0.78) translate(-800 -800)">
        <Wreath cx={800} cy={800} lit={lit} />
      </g>
    </>
  ),
  nikolaus: () => (
    <>
      <Sky id="sky-nikolaus" top="night-top" bottom="night-bottom" />
      <Stars />
      <Snow />
      <Boot x={1300} base={900} />
    </>
  ),
  christmas: () => (
    <>
      <Sky id="sky-christmas" top="night-top" bottom="night-bottom" />
      <Stars count={60} />
      <Snow count={90} seed={2} />
      <path d="M0 780 Q 400 700 800 780 T 1600 760 V 900 H 0 Z" fill={c('snow')} opacity={0.9} />
      <Tree x={1450} base={880} s={0.85} />
      <Gift x={1210} base={885} w={110} h={90} color="berry" />
      <Gift x={1470} base={885} w={100} h={80} color="balloon3" />
      <Tree x={160} base={820} s={0.45} />
    </>
  ),
  newyearseve: () => (
    <>
      <Sky id="sky-nye" top="night-top" bottom="night-bottom" />
      <Stars />
      <Fireworks />
      <Skyline />
    </>
  ),
  newyear: () => (
    <>
      <Sky id="sky-ny" top="night-top" bottom="night-bottom" />
      <Stars />
      <Fireworks />
      <Confetti count={30} />
      <Skyline />
    </>
  ),
  easter: () => (
    <>
      <Sky id="sky-spring" top="spring-top" bottom="spring-bottom" />
      <circle cx="1380" cy="160" r="90" fill={c('glow')} opacity={0.8} />
      <path d="M0 720 Q 500 600 1000 720 T 1600 680 V 900 H 0 Z" fill={c('meadow-light')} />
      <path d="M0 800 Q 600 720 1200 800 T 1600 790 V 900 H 0 Z" fill={c('meadow')} />
      {[90, 260, 420, 1150, 1330, 1500].map((x, i) => (
        <Flower key={x} x={x} base={850 - (i % 2) * 30} color={BALLOONS[(i + 1) % 5]!} />
      ))}
      <Egg x={640} base={880} color="balloon5" stripe="gold" r={-12} />
      <Egg x={760} base={870} color="balloon2" stripe="berry" r={8} />
      <Egg x={880} base={885} color="balloon3" stripe="snow" r={-4} />
      <Egg x={1000} base={875} color="berry" stripe="gold" r={14} />
    </>
  ),
  halloween: () => (
    <>
      <Sky id="sky-halloween" top="party-top" bottom="night-bottom" />
      <Stars />
      <circle cx="1300" cy="200" r="110" fill={c('glow')} />
      <Pumpkin x={260} base={900} />
      <Pumpkin x={520} base={900} s={0.7} />
      <Pumpkin x={1380} base={900} s={0.85} />
    </>
  ),
};

/** Small motifs for the band: one object, no sky. */
function Badge({ kind, lit }: { kind: FestiveKind; lit: number }) {
  switch (kind) {
    case 'birthday':
      return (
        <svg viewBox="1210 610 380 300" aria-hidden className="h-full w-auto">
          <Cake x={1400} base={900} />
        </svg>
      );
    case 'advent':
      return (
        <svg viewBox="450 520 700 380" aria-hidden className="h-full w-auto">
          <Wreath cx={800} cy={800} lit={lit} />
        </svg>
      );
    case 'christmas':
      return (
        <svg viewBox="1080 230 500 670" aria-hidden className="h-full w-auto">
          <Tree x={1330} base={860} />
        </svg>
      );
    case 'easter':
      return (
        <svg viewBox="580 790 480 110" aria-hidden className="h-full w-auto">
          <Egg x={640} base={880} color="balloon5" stripe="gold" r={-12} />
          <Egg x={760} base={870} color="balloon2" stripe="berry" r={8} />
          <Egg x={880} base={885} color="balloon3" stripe="snow" r={-4} />
          <Egg x={1000} base={875} color="berry" stripe="gold" r={14} />
        </svg>
      );
    case 'halloween':
      return (
        <svg viewBox="160 690 200 210" aria-hidden className="h-full w-auto">
          <Pumpkin x={260} base={900} />
        </svg>
      );
    case 'nikolaus':
      return (
        <svg viewBox="1190 540 420 360" aria-hidden className="h-full w-auto">
          <Boot x={1300} base={900} />
        </svg>
      );
    default:
      return (
        <svg viewBox="200 30 1200 420" aria-hidden className="h-full w-auto">
          <Fireworks />
        </svg>
      );
  }
}

export function FestiveScene({
  kind,
  variant = 'backdrop',
  candles = 4,
  className,
}: FestiveSceneProps) {
  if (variant === 'badge') {
    return (
      <span aria-hidden className={cn('inline-flex h-12 shrink-0', className)}>
        <Badge kind={kind} lit={candles} />
      </span>
    );
  }
  return (
    <svg
      viewBox="0 0 1600 900"
      preserveAspectRatio={variant === 'card' ? 'xMidYMax slice' : 'xMidYMax slice'}
      aria-hidden
      className={cn(
        'block',
        variant === 'backdrop' ? 'absolute inset-0 size-full' : 'w-full',
        className,
      )}
    >
      {SCENES[kind](candles)}
    </svg>
  );
}
